// src/components/admin/ActivityForm.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../../styles/admin/ActivityForm.css';
import { API_BASE_URL } from '../../config'

interface ContentBlock {
  type: 'paragraph' | 'images';
  content: string;
  order: number;
}

interface Activity {
  title: string;
  date: string;
  location: string;
  content: ContentBlock[];
  status: 'draft' | 'published';
}

interface ImageData {
  url: string;
  id: string;
  // Add this to track new uploads that haven't been saved yet
  isTemporary?: boolean;
}

const ActivityForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  
  // Keep track of temporary image URLs that need deletion if form is canceled
  const [temporaryImageUrls, setTemporaryImageUrls] = useState<string[]>([]);
  // Keep track of images to be removed on save
  const [imagesToRemove, setImagesToRemove] = useState<string[]>([]);

  const [activity, setActivity] = useState<Activity>({
    title: '',
    date: '',
    location: '',
    content: [],
    status: 'draft'
  });

  // Track if the component is mounted to avoid state updates after unmount
  const isMounted = useRef(true);

  // Format date to YYYY-MM-DD for input type="date"
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    
    // Check if the date is already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return ''; // Invalid date
      
      // Format to YYYY-MM-DD
      return date.toISOString().split('T')[0];
    } catch (e) {
      console.error('Error formatting date:', e);
      return '';
    }
  };

  useEffect(() => {
    // Reset on every mount — React StrictMode mounts twice in dev (mount→unmount→mount),
    // and without this reset the ref stays false, leaving the loading spinner stuck.
    isMounted.current = true;
    if (id) {
      fetchActivity();
    } else {
      // Initialize with empty paragraph for new activity
      setActivity(prev => ({
        ...prev,
        content: [{ type: 'paragraph', content: '', order: 0 }]
      }));
    }

    // Cleanup function
    return () => {
      isMounted.current = false;
      // Attempt to cleanup any temporary images if the component unmounts
      cleanupTemporaryImages();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchActivity = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/activities/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch activity');
      }

      const data = await response.json();
      
      // Format the date for the input
      const formattedData = {
        ...data,
        date: formatDateForInput(data.date)
      };
      
      setActivity(formattedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setActivity(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBlockContentChange = (index: number, value: string) => {
    setActivity(prev => ({
      ...prev,
      content: prev.content.map((block, i) =>
        i === index ? { ...block, content: value } : block
      )
    }));
  };

  const addBlock = (type: 'paragraph' | 'images') => {
    setActivity(prev => ({
      ...prev,
      content: [
        ...prev.content,
        {
          type,
          content: type === 'images' ? JSON.stringify([]) : '',
          order: prev.content.length
        }
      ]
    }));
  };

  const removeBlock = (index: number) => {
    // Check if the block is an image block
    const block = activity.content[index];
    if (block.type === 'images') {
      // Add any temporary images from this block to our cleanup list
      try {
        const images = JSON.parse(block.content || '[]') as ImageData[];
        
        // Add temporary images to cleanup list
        const tempImages = images.filter(img => img.isTemporary).map(img => img.url);
        if (tempImages.length > 0) {
          setTemporaryImageUrls(prev => [...prev, ...tempImages]);
        }
        
        // Add permanent images to removal list for when we save
        const permImages = images.filter(img => !img.isTemporary).map(img => img.url);
        if (permImages.length > 0) {
          setImagesToRemove(prev => [...prev, ...permImages]);
        }
      } catch (e) {
        console.error('Error parsing image content:', e);
      }
    }

    setActivity(prev => ({
      ...prev,
      content: prev.content.filter((_, i) => i !== index)
    }));
  };

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === activity.content.length - 1) return;

    const newContent = [...activity.content];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newContent[index], newContent[targetIndex]] = [newContent[targetIndex], newContent[index]];

    setActivity(prev => ({
      ...prev,
      content: newContent.map((block, i) => ({ ...block, order: i }))
    }));
  };

  const handleImageUpload = async (blockIndex: number, file: File) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const { url } = await response.json();

      // Add newly uploaded URL to temporary list
      setTemporaryImageUrls(prev => [...prev, url]);

      // Add image to block's images array (only in memory, not saved to DB yet)
      const block = activity.content[blockIndex];
      const currentImages = JSON.parse(block.content || '[]') as ImageData[];

      handleBlockContentChange(blockIndex, JSON.stringify([
        ...currentImages,
        { url, id: Date.now().toString(), isTemporary: true }
      ]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (blockIndex: number, imageId: string) => {
    const block = activity.content[blockIndex];
    const currentImages = JSON.parse(block.content || '[]') as ImageData[];
    
    // Find the image to be removed
    const imageToRemove = currentImages.find(img => img.id === imageId);
    
    if (imageToRemove) {
      // If it's a temporary image, add it to cleanup list
      if (imageToRemove.isTemporary) {
        setTemporaryImageUrls(prev => [...prev, imageToRemove.url]);
      } else {
        // If it's a permanent image, add to removal list for when we save
        setImagesToRemove(prev => [...prev, imageToRemove.url]);
      }
    }
    
    const updatedImages = currentImages.filter(img => img.id !== imageId);
    handleBlockContentChange(blockIndex, JSON.stringify(updatedImages));
  };

  // Function to clean up all temporary images
  const cleanupTemporaryImages = async () => {
    const imagesToCleanup = [...temporaryImageUrls];
    if (imagesToCleanup.length === 0) return;
    
    try {
      // Create a new endpoint to handle deletion of temporary images
      await fetch(`${API_BASE_URL}/api/cleanup-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ urls: imagesToCleanup }),
      });
      
      // Clear the temporary URLs list
      setTemporaryImageUrls([]);
    } catch (err) {
      console.error('Failed to clean up temporary images:', err);
    }
  };

  const handleCancel = async () => {
    await cleanupTemporaryImages();
    navigate('/admin/activities');
  };

  const handleSubmit = async (publish: boolean = false) => {
    try {
      setLoading(true);
      
      // First, clean up any images that were removed during editing
      if (imagesToRemove.length > 0) {
        await fetch(`${API_BASE_URL}/api/cleanup-images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ urls: imagesToRemove }),
        });
      }
      
      const method = id ? 'PUT' : 'POST';
      const url = id ? `${API_BASE_URL}/api/activities/${id}` : `${API_BASE_URL}/api/activities`;

      // Mark all images as permanent by removing isTemporary flag
      const updatedContent = activity.content.map(block => {
        if (block.type === 'images') {
          try {
            const images = JSON.parse(block.content || '[]') as ImageData[];
            // Remove isTemporary flag from all images
            const updatedImages = images.map(img => {
              const { isTemporary, ...rest } = img;
              return rest;
            });
            return { ...block, content: JSON.stringify(updatedImages) };
          } catch (e) {
            console.error('Error parsing image content:', e);
            return block;
          }
        }
        return block;
      });

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...activity,
          content: updatedContent,
          status: publish ? 'published' : activity.status
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save activity');
      }

      // Clear temporary URLs list since they're now permanent
      setTemporaryImageUrls([]);
      setImagesToRemove([]);
      
      navigate('/admin/activities');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save activity');
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  if (loading && id) {
    return (
      <div className="activity-form-loading">
        <div className="loading-spinner"></div>
        <p>Loading activity...</p>
      </div>
    );
  }

  return (
    <div className="activity-form">
      <div className="activity-form-header">
        <h1>{id ? 'Edit Activity' : 'Create New Activity'}</h1>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <div className="form-group">
          <label htmlFor="title">Activity Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={activity.title}
            onChange={handleInputChange}
            placeholder="Enter activity title"
            className="form-control"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="date">Activity Date</label>
          <input
            type="date"
            id="date"
            name="date"
            value={activity.date}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="location">Location</label>
          <input
            type="text"
            id="location"
            name="location"
            value={activity.location}
            onChange={handleInputChange}
            placeholder="Enter location"
            className="form-control"
            required
          />
        </div>

        <div className="form-group">
          <label>Activity Content</label>
          <div className="rich-editor">
            <div className="editor-toolbar">
              <span>Use the add block buttons below to add content</span>
            </div>

            <div className="editor-content">
              {activity.content.map((block, index) => (
                <div
                  key={index}
                  className={`content-block ${block.type}-block`}
                >
                  <span className="block-type-badge">{block.type}</span>

                  {block.type === 'paragraph' && (
                    <textarea
                      value={block.content}
                      onChange={(e) => handleBlockContentChange(index, e.target.value)}
                      placeholder="Enter paragraph text..."
                      className="paragraph-input"
                    />
                  )}

                  {block.type === 'images' && (
                    <div className="image-block-content">
                      {JSON.parse(block.content || '[]').map((image: ImageData, imageIndex: number) => (
                        <div key={`${index}-${image.id || imageIndex}`} className="image-container">
                          <img src={image.url} alt="Activity" />
                          <button
                            type="button"
                            onClick={() => removeImage(index, image.id)}
                            className="remove-image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <div className={`add-image-btn ${uploading ? 'uploading' : ''}`}>
                        <input
                          type="file"
                          accept="image/*"
                          id={`image-upload-${index}`}
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(index, file);
                          }}
                        />
                        <label htmlFor={`image-upload-${index}`}>
                          {uploading ? (
                            <>
                              <div className="upload-spinner"></div>
                              <div>Uploading...</div>
                            </>
                          ) : (
                            <>
                              <div>+ Add Image</div>
                              <small>Click to upload</small>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="block-controls">
                    <button
                      type="button"
                      className="control-btn move-up"
                      onClick={() => moveBlock(index, 'up')}
                      disabled={index === 0 || loading || uploading}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="control-btn move-down"
                      onClick={() => moveBlock(index, 'down')}
                      disabled={index === activity.content.length - 1 || loading || uploading}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="control-btn delete"
                      onClick={() => removeBlock(index)}
                      disabled={loading || uploading}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}

              <div className="add-block-section">
                <button
                  type="button"
                  className="add-block-btn"
                  onClick={() => addBlock('paragraph')}
                  disabled={loading || uploading}
                >
                  + Add Paragraph
                </button>
                <button
                  type="button"
                  className="add-block-btn"
                  onClick={() => addBlock('images')}
                  disabled={loading || uploading}
                >
                  + Add Images
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            name="status"
            value={activity.status}
            onChange={handleInputChange}
            className="form-control"
            disabled={loading || uploading}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>

        <div className="preview-toggle">
          <button
            type="button"
            onClick={() => setIsPreview(!isPreview)}
            className="preview-btn"
            disabled={loading || uploading}
          >
            {isPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>

        {isPreview && (
          <div className="preview-content">
            <h2>{activity.title || 'Untitled Activity'}</h2>
            <div className="preview-meta">
              <span>📅 {activity.date ? new Date(activity.date).toLocaleDateString() : 'Date not set'}</span>
              <span>📍 {activity.location || 'Location not set'}</span>
            </div>
            {activity.content.map((block, index) => (
              <div key={index} className="preview-block">
                {block.type === 'paragraph' && (
                  <p style={{ whiteSpace: 'pre-wrap' }}>{block.content}</p>
                )}
                {block.type === 'images' && (
                  <div className="preview-images">
                    {JSON.parse(block.content || '[]').map((image: ImageData, imageIndex: number) => (
                      <img
                        key={`preview-${index}-${image.id || imageIndex}`}
                        src={image.url}
                        alt="Activity"
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="btn btn-save"
            disabled={loading || uploading}
          >
            {loading ? 'Saving...' : id ? 'Update as Draft' : 'Save as Draft'}
          </button>
          {(activity.status === 'draft' || !id) && (
            <button
              type="button"
              className="btn btn-publish"
              onClick={() => handleSubmit(true)}
              disabled={loading || uploading}
            >
              {loading ? 'Publishing...' : id ? 'Update & Publish' : 'Save & Publish'}
            </button>
          )}
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handleCancel}
            disabled={loading || uploading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ActivityForm;